import * as React from 'react';
import TextField, { TextFieldProps } from '@mui/material/TextField';
import cn from 'classnames';
import InputAdornment from '@mui/material/InputAdornment';
import { InputBaseComponentProps, InputBaseProps } from '@mui/material/InputBase';
import { useTheme } from '../theme';
import { InputProps as StandardInputProps } from '@mui/material/Input/Input';

// #fixme: Переделать на дефолтные MUI props.
export interface Props {
	placeholder?: string;
	label?: string;
	value?: string;
	name?: string;
	className?: string;
	labelClassName?: string;
	labelFocusedClassName?: string;
	inputProps?: InputBaseProps['inputProps'];
	inputClassName?: string;
	inputErrorClassName?: string;
	isLoading?: boolean;
	TextFieldProps?: TextFieldProps;
	inputComponent?: React.ElementType<InputBaseComponentProps>;
	absoluteHintPosition?: boolean;
	disabled?: boolean;
	focused?: boolean;
	shrink?: boolean;
	inputRef?: React.Ref<HTMLInputElement>;
	endAdornment?: React.ReactNode;
	errorHintClassName?: string;
	tabIndex?: number;
	autoFocus?: boolean;
	inputFocusedClassName?: string;
	labelShrinkClassName?: string;
	variant?: 'outlined' | 'filled' | 'standard';
	labelErrorClassName?: string;
	inputMode?: InputBaseComponentProps['inputMode'];
	autoComplete?: string;
}

const loaderIcon = (
	<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
		<rect width="24" height="24" fill="white" />
		<circle opacity="0.3" cx="12" cy="12" r="9" stroke="url(#paint0_angular)" strokeWidth="2" />
		<defs>
			<radialGradient
				id="paint0_angular"
				cx="0"
				cy="0"
				r="1"
				gradientUnits="userSpaceOnUse"
				gradientTransform="translate(12 12) rotate(90) scale(10)"
			>
				<stop stopOpacity="0" />
				<stop offset="1" />
			</radialGradient>
		</defs>
	</svg>
);

const Input: React.FunctionComponent<Props> = props => {
	const { Input: theme } = useTheme('Input');

	const variant = props.variant || 'filled';

	return (
		<TextField
			fullWidth={true}
			placeholder={props.placeholder}
			label={props.label}
			disabled={props.disabled}
			variant={variant}
			focused={props.focused}
			inputProps={{
				autoComplete: props.autoComplete ?? 'one-time-code',
				autoCorrect: 'off',
				autoCapitalize: 'off',
				autoFocus: props.autoFocus,
				spellCheck: false,
				tabIndex: props.tabIndex ? props.tabIndex : null,
				className: theme.real_input,
				inputMode: props.inputMode,
				...props.inputProps
			}}
			FormHelperTextProps={{
				classes: {
					error: cn(cn(theme.hint, props.errorHintClassName), {
						[theme.hint_error]: props.absoluteHintPosition
					})
				}
			}}
			value={props.value}
			name={props.name}
			{...props.TextFieldProps}
			className={cn(theme.root, props.className, props?.TextFieldProps?.className, {
				[theme.valueFilled]: props.TextFieldProps?.value
			})}
			InputLabelProps={{
				classes: {
					root: cn(theme.textField__root, props.labelClassName, {
						[theme.textField_standard]: variant === 'standard'
					}),
					focused: cn(theme.textField, theme.textField_focused, props.labelFocusedClassName),
					error: cn(theme.label_error, props.labelErrorClassName),
					shrink: cn(theme.label_shrink, props.labelShrinkClassName),
					outlined: theme.textField_outlined,
					filled: theme.textField_filled,
					...props.TextFieldProps?.InputLabelProps?.classes
				},
				shrink: props.shrink
			}}
			InputProps={
				{
					endAdornment: props.isLoading ? (
						<InputAdornment position="end" className={theme.loader}>
							{loaderIcon}
						</InputAdornment>
					) : (
						props.endAdornment
					),
					inputComponent: props.inputComponent,
					inputRef: props.inputRef,
					disableUnderline: variant !== 'standard',
					inputMode: props.inputMode,
					...props?.TextFieldProps?.InputProps,
					classes: {
						disabled: theme.disabled,
						focused: cn(theme.input_focused, props.inputFocusedClassName),
						root: cn(theme.input, props.inputClassName, {
							[theme.input_filled]: variant === 'filled',
							[theme.input_outline]: variant === 'outlined',
							[theme.input_standard]: variant === 'standard'
						}),
						underline: theme.underline,
						notchedOutline: variant === 'outlined' ? theme.input_outline : null,
						error: cn(theme.input_error, props.inputErrorClassName),
						...props.TextFieldProps?.InputProps?.classes
					}
				} as StandardInputProps
			}
		/>
	);
};

export default Input;
